/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { dynamicSort } from "../../utils/MainHelper.js";
import MainServices from "../../services/Main.services";
import ConfigServices from "../../services/Config.services.js";

export const getMarketPackages = createAsyncThunk(
    "buildTargets/getMarketPackages",
    async (userId, thunkAPI) => {
        const {
            data,
            isSuccess,
            message: msg
        } = await MainServices.getPermission("marketpackage", {}, "permMarketPackage");
        if (isSuccess) {
            const tempCountryObj = {}
            const marketPackageList = Object.values(data).reduce((accMP, MPObject) => {
                if (MPObject.isActive) {
                    const tempMP = {
                        ...MPObject,
                        key: `${MPObject.id}~~${MPObject.code}`,
                        children: Object.values(MPObject.module).reduce((accModule, ModuleObject) => {
                            if (MPObject.isActive) {
                                const tempModule = {
                                    ...ModuleObject,
                                    key: `${ModuleObject.id}~~${ModuleObject.code}`,
                                }
                                tempCountryObj[ModuleObject.code] = Object.values(tempModule.country).sort(dynamicSort("name"))
                                delete tempModule.country
                                accModule.push(tempModule)
                            }
                            return accModule
                        }, []).sort(dynamicSort("sequenceNo", true))
                    }
                    delete tempMP.module
                    accMP.push(tempMP)
                }
                return accMP
            }, []).sort(dynamicSort("sequenceNo", true))
            if (marketPackageList.length) {
                return {
                    marketPackages: marketPackageList,
                    analysisAccessPerModule: {},
                    quickReportsAndCountry: {},
                    countryListObj: tempCountryObj
                };
            } else {
                console.log("No Market Package Access.")
            }
        } else {
            console.log(msg)
        }
        return {
            marketPackages: [],
            analysisAccessPerModule: {},
            quickReportsAndCountry: {},
            countryListObj: {}
        };
    }
);

export const getQuickReports = createAsyncThunk(
    "buildTargets/getQuickReports",
    async (userId, thunkAPI) => {
        const {
            data,
            isSuccess,
            message: msg
        } = await MainServices.getPermission("quickreport", {}, "permQuickReport");
        if (isSuccess) {
            return data
        }
        console.log(msg)
        return {}
    }
);

export const getAnalysisList = createAsyncThunk(
    "buildTargets/getAnalysisList",
    async (userId, thunkAPI) => {
        const {
            data,
            isSuccess,
            message: msg
        } = await MainServices.getPermission("analysis", {}, "permAnalysis");
        if (isSuccess) {
            ConfigServices.analysisObjectListTemp = Object.values(data).reduce((acc, mod) => ({
                ...acc,
                ...(mod?.analysis || {})
            }), {})
            return data
        }
        return {}
    }
);

const buildTargets = createSlice({
    name: "buildTargets",
    initialState: {
        isAppLoaded: false,
        marketPackages: [],
        analysisObj: {},
        countryListObj: {},
        quickReportsAndCountry: {},
        analysisAccessPerModule: {},
        setup: {
            marketPackage: null,
            country: null
        },
        editTarget: false,
        countryNotAvailable: false,
        loadingServers: {},
        hasAccessToInsightsFeature: false,
        hasAccessToPersonasFeature: false,
        hasAccessToScrapeTopicBrandFeature: false,
        scrapeTopicBrand: [],
        scrapeViewer: false,
        isWhitelabel: false
    },
    reducers: {
        setIsAppLoaded: (state, action) => {
            state.isAppLoaded = action.payload
        },
        setHasAccessToInsightsFeature: (state, action) => {
            state.hasAccessToInsightsFeature = action.payload
        },
        setHasAccessToPersonasFeature: (state, action) => {
            state.hasAccessToPersonasFeature = action.payload
        },
        setIsWhitelabel: (state, action) => {
            state.isWhitelabel = action.payload
        },
        setHasAccessToScrapeTopicBrandFeature: (state, action) => {
            state.hasAccessToScrapeTopicBrandFeature = action.payload
        },
        setScrapeTopicBrand: (state, action) => {
            state.scrapeTopicBrand = action.payload
        },
        setScrapeViewer: (state, action) => {
            state.scrapeViewer = action.payload
        },
        setDateList: (state, action) => ({ ...state, dateList: action.payload }),
        setQuickReportsAndCountry: (state, action) => ({ ...state, quickReportsAndCountry: action.payload }),
        setAnalysisAccessPerModule: (state, action) => ({ ...state, analysisAccessPerModule: action.payload }),
        setSetupMarketPackage: (state, action) => ({
            ...state,
            setup: {
                ...state.setup,
                marketPackage: action.payload
            }
        }),
        setSetupDate: (state, action) => ({
            ...state,
            setup: {
                ...state.setup,
                date: action.payload
            }
        }),
        setServerLoading: (state, action) => {
            const { key, value } = action.payload;
            state.loadingServers[key] = value;
        },
        setSetuCountry: (state, action) => ({
            ...state,
            setup: {
                ...state.setup,
                country: action.payload
            }
        }),
        setEditTarget: (state, action) => ({
            ...state,
            editTarget: action.payload
        }),
        setCountryNotAvailable: (state, action) => {
            state.countryNotAvailable = action.payload;
        },
        setBuildTargetFromLoadedJson: (state, action) => {
            Object.keys(action.payload).forEach((key) => {
                state[key] = action.payload[key];
            });
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getMarketPackages.fulfilled, (state, action) => ({
            ...state,
            marketPackages: action.payload.marketPackages,
            countryListObj: action.payload.countryListObj,
            quickReportsAndCountry: action.payload.quickReportsAndCountry,
            analysisAccessPerModule: action.payload.analysisAccessPerModule
        })).addCase(getQuickReports.fulfilled, (state, action) => ({
            ...state,
            quickReportsAndCountry: action.payload
        })).addCase(getAnalysisList.fulfilled, (state, action) => ({
            ...state,
            analysisObj: action.payload
        }));
    }
});

const { reducer, actions } = buildTargets;
export const {
    setDateList, setQuickReportsAndCountry, setAnalysisAccessPerModule,
    setSetupMarketPackage, setSetupDate, setSetuCountry, setEditTarget, setBuildTargetFromLoadedJson, setCountryNotAvailable,
    setServerLoading,
    setHasAccessToInsightsFeature,
    setHasAccessToPersonasFeature,
    setHasAccessToScrapeTopicBrandFeature,
    setScrapeTopicBrand,
    setScrapeViewer,
    setIsWhitelabel,
    setIsAppLoaded
} = actions;
export default reducer;
